import { apiRequest } from '../../shared/api/client';

/**
 * Users — mirrors Invetrak.Api.Endpoints.UserManagementEndpoints.
 *   GET    /api/v1/users                              tenant:manage_users
 *   GET    /api/v1/groups                              tenant:manage_users
 *   GET    /api/v1/users/{userId}/groups               tenant:manage_users
 *   POST   /api/v1/users/{userId}/groups               tenant:manage_users
 *   DELETE /api/v1/users/{userId}/groups/{groupCode}    tenant:manage_users
 *
 * One permission covers all of it. Who may work in this tenant, in what group, and in which
 * building are the same decision, and splitting them would let somebody grant a group they could
 * not then scope.
 *
 *   POST   /api/v1/users/invite                       tenant:manage_users
 *   GET    /api/v1/users/invitations                  tenant:manage_users
 *   DELETE /api/v1/users/invitations/{id}             tenant:manage_users
 *
 * A tenant does not CREATE users; it invites them. The invitee sets their own password, which is
 * why there is no "new user" form anywhere — an admin who typed somebody's password would know it.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface TenantUserDto {
  tenantUserId: string;
  userId: string;
  email: string;
  displayName: string;
  isActive: boolean;
  activeGroupCodes: string[];
  /**
   * How many warehouses this user can reach. Zero is the value worth noticing: they can sign in
   * and do nothing, and the app shows them a "not set up yet" screen.
   */
  warehouseCount: number;
  /**
   * True for a tenant administrator, and for anyone holding a grant that names no warehouse. The
   * screen says "All" rather than a count, which would otherwise change under the reader every
   * time somebody added a warehouse.
   */
  hasAllWarehouses: boolean;
}

/**
 * One group this user is in, and the warehouse it applies in.
 *
 * A null warehouseId is an UNSCOPED grant — the group applies everywhere. Rendered as "All
 * warehouses"; rendered as a blank it reads as a broken row.
 */
export interface UserGroupGrantDto {
  groupId: string;
  groupCode: string;
  groupName: string;
  isLocked: boolean;
  warehouseId: string | null;
  warehouseCode: string | null;
  warehouseName: string | null;
  grantedAtUtc: string;
  grantedByUserId: string | null;
  grantedByName: string | null;
}

export interface GroupDto {
  groupId: string;
  groupCode: string;
  displayName: string;
  /**
   * Locked means the code, the name and the permission set are all fixed and the group cannot be
   * deleted. Tenant Admin is locked because it holds every permission: a tenant that stripped it
   * would have no way to grant anything back, including the permission to repair it.
   */
  isLocked: boolean;
  isActive: boolean;
  /** Zero grants nothing — worth seeing without opening the group. */
  permissionCount: number;
  /** Zero makes it a candidate for deletion. */
  memberCount: number;
}

/**
 * One permission and whether this group grants it.
 *
 * EVERY permission comes back, not only the granted ones, so the editor renders a full set of
 * checkboxes from one call — legacy's sp_WebGroupPermissionMap_GetForGroupEdit does the same.
 */
export interface PermissionGrantDto {
  permissionId: string;
  permissionCode: string;
  displayName: string;
  category: string;
  isGranted: boolean;
}

export interface SaveGroupRequest {
  groupCode: string;
  displayName: string;
  isActive?: boolean;
}

export async function listUsers(signal?: AbortSignal): Promise<TenantUserDto[]> {
  const { data } = await apiRequest<TenantUserDto[]>('/v1/users/', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listGroups(signal?: AbortSignal): Promise<GroupDto[]> {
  const { data } = await apiRequest<GroupDto[]>('/v1/groups/', {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** Every active grant for one user: which group, in which warehouse. */
export async function listUserGroups(
  userId: string,
  signal?: AbortSignal,
): Promise<UserGroupGrantDto[]> {
  const { data } = await apiRequest<UserGroupGrantDto[]>(
    `/v1/users/${encodeURIComponent(userId)}/groups`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Puts a user in a group, in one warehouse or in all of them.
 *
 * `warehouseId` null grants it EVERYWHERE — that is how you say "this person works in all our
 * sites", so it is a deliberate choice rather than a harmless default.
 */
export async function grantGroup(
  userId: string,
  groupCode: string,
  warehouseId: string | null,
  signal?: AbortSignal,
): Promise<void> {
  await apiRequest<void>(`/v1/users/${encodeURIComponent(userId)}/groups`, {
    method: 'POST',
    body: { groupCode, warehouseId },
    signal,
    ...COOKIE_INIT,
  });
}

/**
 * Takes a user out of a group in ONE scope.
 *
 * `warehouseId` null revokes the unscoped grant only — NOT every scoped one. Otherwise removing
 * somebody from Reno would quietly remove them from Dallas too.
 */
export async function revokeGroup(
  userId: string,
  groupCode: string,
  warehouseId: string | null,
  signal?: AbortSignal,
): Promise<void> {
  const query = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
  await apiRequest<void>(
    `/v1/users/${encodeURIComponent(userId)}/groups/${encodeURIComponent(groupCode)}${query}`,
    { method: 'DELETE', signal, ...COOKIE_INIT },
  );
}

export async function saveGroup(
  groupId: string | null,
  body: SaveGroupRequest,
  signal?: AbortSignal,
): Promise<GroupDto> {
  const path = groupId ? `/v1/groups/${encodeURIComponent(groupId)}` : '/v1/groups/';
  const { data } = await apiRequest<GroupDto>(path, {
    method: groupId ? 'PUT' : 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function deleteGroup(groupId: string, signal?: AbortSignal): Promise<void> {
  await apiRequest<void>(`/v1/groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
    signal,
    ...COOKIE_INIT,
  });
}

export async function listGroupPermissions(
  groupId: string,
  signal?: AbortSignal,
): Promise<PermissionGrantDto[]> {
  const { data } = await apiRequest<PermissionGrantDto[]>(
    `/v1/groups/${encodeURIComponent(groupId)}/permissions`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Whole-set: what this group should grant. Anything absent is revoked, so an empty array means
 * "grants nothing" rather than "leave it alone".
 */
export async function setGroupPermissions(
  groupId: string,
  permissionIds: string[],
  signal?: AbortSignal,
): Promise<PermissionGrantDto[]> {
  const { data } = await apiRequest<PermissionGrantDto[]>(
    `/v1/groups/${encodeURIComponent(groupId)}/permissions`,
    { method: 'PUT', body: { permissionIds }, signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * A pending or finished invitation.
 *
 * `status` is pending / accepted / cancelled / expired. The list defaults to pending, because the
 * finished ones are history rather than work.
 */
/**
 * One group an invitation grants, and where it applies.
 *
 * A null warehouseId means EVERY warehouse. Before central 019 an invitation could not say
 * anything else, so inviting a picker for one 3PL client silently gave them every client's site.
 */
export interface InvitationGrantDto {
  groupCode: string;
  warehouseId: string | null;
}

export interface InvitationDto {
  tenantInvitationId: string;
  email: string;
  expiresAtUtc: string;
  grants: InvitationGrantDto[];
  status: string;
  createdAtUtc: string;
  consumedAtUtc: string | null;
  invitedByUserId: string | null;
  invitedByEmail: string | null;
}

export interface InviteResponseDto {
  tenantInvitationId: string;
  email: string;
  expiresAtUtc: string;
  grants: InvitationGrantDto[];
}

/**
 * Invites somebody to this tenant.
 *
 * The server emails them a link; the grants travel with the invitation so they arrive able to
 * work rather than able to sign in and do nothing. Each grant carries the warehouse it applies
 * in — an invitation cannot be edited once it is out, so getting that wrong means cancelling and
 * re-issuing it.
 */
export async function inviteUser(
  email: string,
  grants: InvitationGrantDto[],
  signal?: AbortSignal,
): Promise<InviteResponseDto> {
  const { data } = await apiRequest<InviteResponseDto>('/v1/users/invite', {
    method: 'POST',
    body: { email, grants },
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listInvitations(
  includeAll = false,
  signal?: AbortSignal,
): Promise<InvitationDto[]> {
  const { data } = await apiRequest<InvitationDto[]>(
    `/v1/users/invitations${includeAll ? '?includeAll=true' : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function cancelInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
  await apiRequest<void>(`/v1/users/invitations/${encodeURIComponent(invitationId)}`, {
    method: 'DELETE',
    signal,
    ...COOKIE_INIT,
  });
}
